import React, { createContext, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDynamicFormDrawerStyles } from '@components/dynamic-form/style';
import { preventBackdropDrawerClose } from '@components/generic-drawer/drawerCloseHandler';
import CloseIcon from '@mui/icons-material/Close';
import { Drawer, IconButton } from '@mui/material';
import { MAIN_THEME } from '@styles';

import { AccountingTransferDrawer } from './accountingTransfer.drawer';
import { AmendmentDrawer } from './amendment.drawer';
import { CloseMonthDrawer } from './closeMonth.drawer';
import { DeactivateProviderDrawer } from './deactivateProvider.drawer';
import { GenerateClosingReport } from './generateClosingReport.drawer';
import { ModifyAmendmentDetailsDrawer } from './modifyAmendmentDetails.drawer';
import { ModifyProviderDrawer } from './modifyProvider.drawer';
import { ProviderDrawer } from './provider.drawer';

type DrawerState = {
  isOpen: boolean;
  name: string;
  title: string;
  props?: any;
};

type DrawerContextType = {
  drawer: DrawerState;
  openDrawer: (name: string, title: string, props?: any) => void;
  closeDrawer: () => void;
};

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export function useDrawer() {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within DrawerProvider');
  }

  return context;
}

const DrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { classes } = useDynamicFormDrawerStyles();
  const navigate = useNavigate();

  const [drawer, setDrawer] = useState<DrawerState>({
    isOpen: false,
    name: '',
    title: ''
  });

  const openDrawer = useCallback((name: string, title: string, props?: any) => {
    setDrawer({ isOpen: true, name, title, props });
  }, []);

  const closeDrawer = useCallback((urlDetailsPath?: string) => {
    if (urlDetailsPath) {
      navigate(urlDetailsPath);
    }
    setDrawer((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const DrawerHeader = () => {
    return (
      <header className={classes.drawerHeader}>
        <figure className={classes.sectionFigure} />
        <span className={classes.drawerHeaderTitle}>{drawer.title}</span>
        <IconButton
          aria-label="close"
          onClick={() => closeDrawer('')}
          sx={{ margin: '0 15px 0 0' }}
        >
          <CloseIcon sx={{ color: MAIN_THEME.color.white }} />
        </IconButton>
      </header>
    );
  };

  const DrawerBody = () => {
    switch (drawer.name) {
      case 'provider':
        return (
          <ProviderDrawer
            onSuccess={(id: string) =>
              closeDrawer(`salesforce/providers/${id}`)
            }
          />
        );
      case 'updateProvider':
        return (
          <ModifyProviderDrawer
            onSuccess={() => closeDrawer('')}
            data={drawer.props}
          />
        );
      case 'deactivateProvider':
        return (
          <DeactivateProviderDrawer
            onSuccess={closeDrawer}
            data={drawer.props}
          />
        );
      case 'accountingTransfer':
        return (
          <AccountingTransferDrawer
            onSuccess={() => closeDrawer('')}
            companyId={drawer.props?.companyId}
            refetch={drawer.props?.refetch}
          />
        );
      case 'policyAmendment':
        return (
          <AmendmentDrawer
            policyId={drawer?.props?.policyId}
            policyLineCode={drawer?.props?.policyLineCode}
            policyType={drawer?.props?.policyType}
            policyEffectiveDate={drawer?.props?.policyEffectiveDate}
            policyExpiryDate={drawer?.props?.policyExpiryDate}
            policyIssueDate={drawer?.props?.policyIssueDate}
            currencySymbol={drawer?.props?.currencySymbol}
            onSuccess={() => closeDrawer('')}
          />
        );
      case 'modifyAmendmentDetailsDrawer':
        return (
          <ModifyAmendmentDetailsDrawer
            amendmentData={drawer?.props?.amendmentData}
            onSuccess={() => closeDrawer('')}
          />
        );
      case 'closeMonth':
        return (
          <CloseMonthDrawer
            onSuccess={() => closeDrawer('')}
            monthClosingTypeLovs={drawer.props?.monthClosingTypeLovs}
          />
        );
      case 'generateClosingReport':
        return (
          <GenerateClosingReport
            onSuccess={() => closeDrawer('')}
            monthClosingTypeLovs={drawer.props?.monthClosingTypeLovs}
          />
        );
      default:
        return (
          <ProviderDrawer
            onSuccess={() => closeDrawer('')}
            data={drawer.props}
          />
        );
    }
  };

  return (
    <DrawerContext.Provider value={{ drawer, openDrawer, closeDrawer }}>
      {children}
      <Drawer
        open={drawer.isOpen}
        anchor="bottom"
        onClose={preventBackdropDrawerClose(() => closeDrawer(''))}
        classes={{ paperAnchorBottom: classes.drawer }}
      >
        <DrawerHeader />
        <DrawerBody />
      </Drawer>
    </DrawerContext.Provider>
  );
};

export { DrawerProvider };
